import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, BadgeCheck, MapPin, Star, Hammer, Loader2, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatGBP } from '@/lib/utils';

export default function CustomerPartnerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customer/partners/${id}`);
      setPartner(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load partner');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !partner) {
    return <p className="text-center text-sm text-slate-500 py-10"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading...</p>;
  }

  return (
    <div className="space-y-4">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="partner-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <Card className="p-4 border-slate-200">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h2 className="text-lg font-bold text-slate-900 truncate">{partner.business_name}</h2>
              {partner.verified_badge && <BadgeCheck className="h-4 w-4 text-emerald-600" />}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{partner.category} · <MapPin className="h-3 w-3 inline" /> {partner.base_postcode}</p>
          </div>
          {partner.rating != null && (
            <div className="text-right shrink-0">
              <p className="text-sm font-bold text-slate-900 flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {partner.rating}</p>
              <p className="text-[10px] text-slate-400">{partner.review_count} review{partner.review_count === 1 ? '' : 's'}</p>
            </div>
          )}
        </div>
        {partner.bio && <p className="mt-3 text-sm text-slate-600">{partner.bio}</p>}
        <div className="mt-3 flex flex-wrap gap-1">
          {partner.verified_badge && (
            <span className="inline-flex items-center gap-1 text-[10px] rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5">
              <BadgeCheck className="h-2.5 w-2.5" /> Verified
            </span>
          )}
          {partner.category === 'Barber' && partner.cleanup_included && (
            <span className="inline-flex items-center gap-1 text-[10px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5">
              <Hammer className="h-2.5 w-2.5" /> Cleanup included
            </span>
          )}
        </div>
      </Card>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 px-1 mb-2">Services</h3>
        {partner.services.length === 0 ? (
          <Card className="p-4 text-center text-sm text-slate-500">No services listed yet</Card>
        ) : (
          <div className="space-y-2">
            {partner.services.map((s) => (
              <Card key={s.id} className="p-3 border-slate-200" data-testid={`service-${s.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{s.name}</p>
                    <p className="text-[11px] text-slate-500">{s.category} · <Clock className="h-3 w-3 inline" /> {s.duration_minutes} min</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <p className="font-bold text-slate-900">{formatGBP(s.price)}</p>
                    <Button
                      size="sm"
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                      onClick={() => navigate(`/customer/booking/${partner.id}/${s.id}`)}
                      data-testid={`btn-book-svc-${s.id}`}
                    >
                      Book
                    </Button>
                  </div>
                </div>
                {s.description && <p className="text-xs text-slate-500 mt-1">{s.description}</p>}
              </Card>
            ))}
          </div>
        )}
      </div>

      {partner.reviews && partner.reviews.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-900 px-1 mb-2">Reviews</h3>
          <div className="space-y-2">
            {partner.reviews.map((r) => (
              <Card key={r.id} className="p-3 border-slate-200">
                <div className="flex items-center gap-1 text-amber-500">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={`${r.id}-star-${i}`} className="h-3 w-3 fill-amber-400" />
                  ))}
                </div>
                {r.comment && <p className="text-xs text-slate-600 mt-1">{r.comment}</p>}
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
